import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, MessageSquare, Printer, Loader2, ShieldCheck, Mail, Phone, MapPin } from "lucide-react";
import {
  getPublicProposal, recordPublicView, submitPublicDecision, submitPublicChangeRequest,
  generatePublicProposalPdf,
} from "@/lib/proposals.functions";
import { SignaturePad } from "@/components/proposals/SignaturePad";

export const Route = createFileRoute("/proposal/$token")({
  head: () => ({ meta: [{ title: "Your Painting Proposal — Michael's Painting" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: PublicProposal,
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-white text-zinc-900 grid place-items-center px-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl mb-2">Proposal unavailable</h1>
        <p className="text-zinc-400 text-sm">{error.message || "This link is invalid or has expired."}</p>
      </div>
    </div>
  ),
});

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n || 0));

function PublicProposal() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const getFn = useServerFn(getPublicProposal);
  const viewFn = useServerFn(recordPublicView);
  const decisionFn = useServerFn(submitPublicDecision);
  const changeFn = useServerFn(submitPublicChangeRequest);
  const pdfFn = useServerFn(generatePublicProposalPdf);
  const [pdfBusy, setPdfBusy] = useState(false);

  async function openProposalPdf(autoPrint: boolean) {
    if (pdfBusy) return;
    // Open the tab synchronously within the user gesture so browsers
    // don't block the popup after the async PDF generation await.
    const preOpened = autoPrint ? window.open("about:blank", "_blank") : null;
    setPdfBusy(true);
    try {
      const r = await pdfFn({ data: { token } });
      const bin = atob(r.base64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const blob = new Blob([u8], { type: r.mimeType });
      const url = URL.createObjectURL(blob);
      let w: Window | null = preOpened;
      if (w) {
        try { w.location.replace(url); } catch { w = null; }
      } else {
        w = window.open(url, "_blank");
      }
      if (w) {
        if (autoPrint) {
          w.addEventListener("load", () => { try { w.focus(); w.print(); } catch {} });
        }
      } else {
        // Popup was blocked — fall back to a direct download so the user
        // still gets the PDF instead of a silent no-op.
        const a = document.createElement("a");
        a.href = url;
        a.download = r.filename || "proposal.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } finally {
      setPdfBusy(false);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ["proposal-public", token],
    queryFn: () => getFn({ data: { token } }),
  });

  useEffect(() => {
    if (data) viewFn({ data: { token } }).catch(() => {});
  }, [data, token, viewFn]);

  // Note: legacy ?print=1 auto-open removed — browsers block window.open
  // outside a user gesture, so the user must click Print / Save as PDF.

  const [tab, setTab] = useState<"approve" | "decline" | "change" | null>(null);
  const [name, setName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState<string | null>(null);

  const decideMut = useMutation({
    mutationFn: (vars: { decision: "approve" | "decline" }) =>
      decisionFn({ data: { token, decision: vars.decision, name, accepted_terms: accepted, reason, signature: signature || undefined } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["proposal-public", token] }),
  });
  const changeMut = useMutation({
    mutationFn: () => changeFn({ data: { token, name, email, message: msg } }),
    onSuccess: () => {
      setMsg("");
      setTab(null);
      qc.invalidateQueries({ queryKey: ["proposal-public", token] });
    },
  });

  if (isLoading || !data) {
    return <div className="min-h-screen bg-white grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-600" /></div>;
  }

  const p = data.proposal;
  const items = data.line_items as any[];
  const colors = data.colors as any[];
  const company = data.company || {};
  const subtotal = items.reduce((s, i) => s + Number(i.total || 0), 0);
  const total = Number(p.total || subtotal);
  const expired = p.expires_at && new Date(p.expires_at) < new Date();
  const decided = p.status === "approved" || p.status === "declined";

  return (
    <div className="min-h-screen bg-[#fafaf7] text-zinc-900">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-shadow { box-shadow: none !important; border: 0 !important; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-5 py-8 lg:py-12">
        {/* Header / cover */}
        <header className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 lg:p-8 print-shadow">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-amber-700 text-[11px] tracking-[0.25em] uppercase font-semibold">Painting Proposal</p>
              <h1 className="font-serif text-3xl lg:text-4xl mt-2 text-zinc-900">{company.name || "Michael's Painting"}</h1>
              <div className="mt-3 text-xs text-zinc-400 space-y-0.5">
                {company.phone && <p><Phone className="inline size-3 mr-1" />{company.phone}</p>}
                {company.email && <p><Mail className="inline size-3 mr-1" />{company.email}</p>}
                {company.website && <p>{company.website}</p>}
              </div>
            </div>
            <div className="text-right text-xs text-zinc-500">
              <p>Proposal #</p>
              <p className="font-mono text-amber-700 text-base">{p.proposal_number}</p>
              <p className="mt-2">Date: {new Date(p.created_at).toLocaleDateString()}</p>
              {p.expires_at && <p>Expires: {new Date(p.expires_at).toLocaleDateString()}</p>}
              <p className="mt-2 inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 capitalize">{p.status.replace("_"," ")}</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-zinc-200 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Prepared for</p>
              <p className="font-medium">{p.customer_name}</p>
              {p.customer_email && <p className="text-zinc-400 text-xs">{p.customer_email}</p>}
              {p.customer_phone && <p className="text-zinc-400 text-xs">{p.customer_phone}</p>}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Property</p>
              <p className="text-zinc-700">{p.property_address || "—"}</p>
              <p className="text-zinc-500 text-xs mt-1 capitalize">{p.project_type}{p.estimated_timeline ? ` · ${p.estimated_timeline}` : ""}</p>
            </div>
          </div>
        </header>

        {/* Images */}
        {(p.studio_render_url || p.design_image_url || p.cover_image_url) && (
          <Section title="Design preview">
            <div className="grid sm:grid-cols-2 gap-3">
              {p.studio_render_url && <img src={p.studio_render_url} alt="" className="rounded-lg border border-zinc-200" />}
              {p.design_image_url && <img src={p.design_image_url} alt="" className="rounded-lg border border-zinc-200" />}
              {p.cover_image_url && <img src={p.cover_image_url} alt="" className="rounded-lg border border-zinc-200" />}
            </div>
          </Section>
        )}

        {/* Scope */}
        {Array.isArray(p.scope) && p.scope.length > 0 && (
          <Section title="Scope of work">
            <ul className="space-y-1.5 text-sm text-zinc-700">
              {p.scope.map((s: string, i: number) => (
                <li key={i} className="flex gap-2"><span className="text-amber-700 mt-1">•</span><span>{s}</span></li>
              ))}
            </ul>
          </Section>
        )}

        {/* Paint schedule */}
        {colors.length > 0 && (
          <Section title="Paint schedule">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr><th className="text-left py-2">Surface</th><th className="text-left">Color</th><th className="text-left">SW #</th><th className="text-left">Sheen</th><th className="text-left">Coats</th></tr>
                </thead>
                <tbody>
                  {colors.map((c, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="py-2 text-zinc-700">{c.surface}</td>
                      <td className="py-2"><span className="inline-flex items-center gap-2"><span className="inline-block size-4 rounded border border-zinc-300" style={{ background: c.hex || "#fff" }} />{c.color_name || "—"}</span></td>
                      <td className="py-2 text-zinc-500 font-mono text-xs">{c.color_number || "—"}</td>
                      <td className="py-2 text-zinc-400">{c.sheen || "—"}</td>
                      <td className="py-2 text-zinc-400">{c.coats}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Line items */}
        <Section title="Investment">
          <table className="w-full text-sm">
            <tbody>
              {items.map((i, idx) => (
                <tr key={idx} className="border-b border-zinc-100">
                  <td className="py-2.5">
                    <p className="text-zinc-900">{i.title}</p>
                    {i.description && <p className="text-zinc-500 text-xs">{i.description}</p>}
                  </td>
                  <td className="py-2.5 text-right tabular-nums text-zinc-700">{fmt(Number(i.total))}</td>
                </tr>
              ))}
              {p.discount > 0 && (
                <tr className="border-b border-zinc-100">
                  <td className="py-2.5 text-zinc-700">Discount</td>
                  <td className="py-2.5 text-right tabular-nums text-emerald-700">- {fmt(Number(p.discount))}</td>
                </tr>
              )}
              <tr>
                <td className="pt-3 font-semibold text-zinc-900">Total</td>
                <td className="pt-3 text-right font-serif text-2xl text-amber-700">{fmt(total)}</td>
              </tr>
              {p.deposit_required && Number(p.deposit_amount) > 0 && (
                <tr>
                  <td className="pt-1 text-xs text-zinc-500">Deposit to schedule</td>
                  <td className="pt-1 text-right text-sm text-zinc-700 tabular-nums">{fmt(Number(p.deposit_amount))}</td>
                </tr>
              )}
            </tbody>
          </table>
          {p.deposit_required && Number(p.deposit_amount) > 0 && (
            <p className="mt-3 text-xs text-zinc-500">Remaining balance due upon completion of work.</p>
          )}
        </Section>

        {p.warranty && <Section title="Warranty"><p className="text-sm text-zinc-700 whitespace-pre-wrap">{p.warranty}</p></Section>}
        {p.terms && <Section title="Terms & conditions"><p className="text-xs text-zinc-400 whitespace-pre-wrap leading-relaxed">{p.terms}</p></Section>}

        {/* Decision panel */}
        {!decided && !expired && (
          <div className="no-print mt-6 bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
            <h3 className="font-serif text-xl text-zinc-900 mb-3">Your response</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={() => setTab("approve")} className={`text-sm px-4 py-2 rounded-full font-semibold inline-flex items-center gap-1.5 ${tab === "approve" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}><CheckCircle2 className="size-4" /> Approve</button>
              <button onClick={() => setTab("change")} className={`text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5 ${tab === "change" ? "bg-amber-600 text-white" : "bg-amber-50 text-amber-800 hover:bg-amber-100"}`}><MessageSquare className="size-4" /> Request changes</button>
              <button onClick={() => setTab("decline")} className={`text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5 ${tab === "decline" ? "bg-zinc-800 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}><XCircle className="size-4" /> Decline</button>
            </div>

            {tab === "approve" && (
              <div className="space-y-3">
                <label className="block text-sm">
                  <span className="text-zinc-700">Type your full name to sign</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-base" placeholder="Jane Doe" />
                </label>
                <div>
                  <p className="text-sm text-zinc-700 mb-1">Draw your signature</p>
                  <SignaturePad value={signature} onChange={setSignature} theme="light" />
                </div>
                <label className="flex items-start gap-2 text-sm text-zinc-700">
                  <input type="checkbox" className="mt-1" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                  <span>I have read and accept the scope of work, pricing, warranty, and terms above.</span>
                </label>
                <button onClick={() => decideMut.mutate({ decision: "approve" })} disabled={!name || !accepted || !signature || decideMut.isPending}
                  className="bg-emerald-600 text-white font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
                  {decideMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Approve Proposal
                </button>
                {decideMut.error && <p className="text-xs text-red-600">{(decideMut.error as Error).message}</p>}
              </div>
            )}

            {tab === "decline" && (
              <div className="space-y-3">
                <label className="block text-sm">
                  <span className="text-zinc-700">Reason (optional)</span>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                </label>
                <button onClick={() => decideMut.mutate({ decision: "decline" })} disabled={decideMut.isPending}
                  className="bg-zinc-800 text-white font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
                  {decideMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <XCircle className="size-4" />} Decline
                </button>
              </div>
            )}

            {tab === "change" && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-2">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} placeholder="What would you like changed?" className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
                <button onClick={() => changeMut.mutate()} disabled={!msg || changeMut.isPending}
                  className="bg-amber-600 text-white font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50">
                  {changeMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <MessageSquare className="size-4" />} Send request
                </button>
              </div>
            )}
          </div>
        )}

        {decided && (
          <div className="no-print mt-6 bg-white border border-zinc-200 rounded-2xl p-6 text-center">
            {p.status === "approved" ? (
              <>
                <CheckCircle2 className="size-10 text-emerald-600 mx-auto mb-2" />
                <p className="font-serif text-xl">Proposal approved</p>
                <p className="text-sm text-zinc-400">Signed by {p.approved_by_name} on {new Date(p.approved_at).toLocaleString()}.</p>
                {p.signature_data_url && (
                  <img src={p.signature_data_url} alt="Signature" className="mx-auto mt-3 max-h-24" />
                )}
              </>
            ) : (
              <>
                <XCircle className="size-10 text-zinc-700 mx-auto mb-2" />
                <p className="font-serif text-xl">Proposal declined</p>
              </>
            )}
          </div>
        )}

        {expired && !decided && (
          <div className="no-print mt-6 bg-zinc-50 border border-zinc-200 rounded-2xl p-6 text-center text-sm text-zinc-400">
            This proposal has expired. Please contact us for an updated quote.
          </div>
        )}

        <footer className="no-print mt-8 flex items-center justify-between text-xs text-zinc-500">
          <button onClick={() => openProposalPdf(true)} disabled={pdfBusy} className="inline-flex items-center gap-1 hover:text-zinc-800 disabled:opacity-60">
            {pdfBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Printer className="size-3.5" />} Print / Save as PDF
          </button>
          <span className="inline-flex items-center gap-1"><ShieldCheck className="size-3.5" /> Secured by token link</span>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm print-shadow">
      <h2 className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}